import {
  del,
  get,
  axiosConfig,
  type RequestConfig,
} from "../shared/api-client";
import type {
  ApiItemResponse,
  ListQuery,
  PaginatedResult,
} from "@project/types/location";
import type { Product, ProductPayload } from "@project/types/gruh-udhyog";
import {
  JIVANDHARA_BASE,
  SILENT_LIST,
  fetchListPage,
  unwrapItem,
} from "./list-api-helpers";

const BASE = `${JIVANDHARA_BASE}/products`;
const SILENT: RequestConfig = { showNotification: false };

function appendIfPresent(fd: FormData, key: string, value: unknown) {
  if (value === undefined || value === null || value === "") {
    return;
  }
  if (value instanceof File) {
    fd.append(key, value);
    return;
  }
  fd.append(key, String(value));
}

function toFormData(payload: Partial<ProductPayload>): FormData {
  const fd = new FormData();
  appendIfPresent(fd, "category", payload.category);
  appendIfPresent(fd, "variant_master", payload.variant_master);
  appendIfPresent(fd, "product_name", payload.product_name);
  appendIfPresent(fd, "short_description", payload.short_description);
  appendIfPresent(fd, "description", payload.description);
  appendIfPresent(fd, "is_handmade", payload.is_handmade);
  appendIfPresent(fd, "is_active", payload.is_active);
  appendIfPresent(fd, "is_signature", payload.is_signature);
  appendIfPresent(fd, "is_discountable", payload.is_discountable);

  if (payload.variants !== undefined) {
    fd.append("variants", JSON.stringify(payload.variants));
  }

  if (payload.image instanceof File) {
    fd.append("image", payload.image);
  }
  return fd;
}

class GruhUdhyogProductService {
  listPage = (query: ListQuery = {}): Promise<PaginatedResult<Product>> =>
    fetchListPage<Product>(`${BASE}/`, query);

  get = async (id: number): Promise<Product | undefined> => {
    const data = await get<ApiItemResponse<Product> | Product>(
      `${BASE}/${id}/`,
      SILENT_LIST
    );
    return unwrapItem(data);
  };

  create = async (payload: ProductPayload): Promise<Product> => {
    const response = await axiosConfig.post<ApiItemResponse<Product> | Product>(
      `${BASE}/`,
      toFormData(payload),
      {
        ...SILENT,
        headers: { "Content-Type": "multipart/form-data" },
      }
    );
    const item = unwrapItem(response.data);
    if (!item) {
      throw new Error("Failed to create product.");
    }
    return item;
  };

  update = async (
    id: number,
    payload: Partial<ProductPayload>
  ): Promise<Product | undefined> => {
    const response = await axiosConfig.patch<
      ApiItemResponse<Product> | Product
    >(`${BASE}/${id}/`, toFormData(payload), {
      ...SILENT,
      headers: { "Content-Type": "multipart/form-data" },
    });
    return unwrapItem(response.data);
  };

  delete = async (id: number): Promise<void> => {
    await del(`${BASE}/${id}/`, SILENT);
  };

  listLookup = async (search = "") => {
    const result = await this.listPage({
      page: 1,
      page_size: 100,
      search: search || undefined,
      status: "active",
    });
    return result.data.map(item => ({
      value: String(item.id),
      label: item.product_name,
    }));
  };
}

export const gruhUdhyogProductService = new GruhUdhyogProductService();
